import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import NavigationHeader from './components/layout/NavigationHeader';
import ProtectedRoute from './components/auth/ProtectedRoute';
import CustomUrlRedirect from './components/CustomUrlRedirect';
import CartIcon from './components/cart/CartIcon';
import CartSidebar from './components/cart/CartSidebar';
import { isSubdomain, getSubdomainUsername } from './utils/subdomainUtils';
import { customDomains } from './config/customDomains';
import PremiumGate from './components/dashboard/PremiumGate';
import './App.css';

// Critical imports - loaded immediately for fast public profile access
import HomeLegacy from './pages/HomeLegacy';
import PlayerProfilePreview from './pages/PlayerProfilePreview';
import Auth from './pages/Auth';
import NotFound from './pages/NotFound';
import CreatorProfiles from './pages/CreatorProfiles';

// Lazy imports - loaded on demand for authenticated/admin routes
const NewEvent = lazy(() => import('./pages/NewEvent'));
const EventPreview = lazy(() => import('./pages/EventPreview'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Organizer = lazy(() => import('./pages/Organizer'));
const Player = lazy(() => import('./pages/Player'));
const PlayerProfiles = lazy(() => import('./pages/PlayerProfiles'));
const Account = lazy(() => import('./pages/Account'));
const Premium = lazy(() => import('./pages/Premium'));
const AuthTest = lazy(() => import('./pages/AuthTest'));
const Browse = lazy(() => import('./pages/Browse'));
const PremiumSales = lazy(() => import('./pages/PremiumSales'));
const PremiumPlayerSales = lazy(() => import('./pages/PremiumPlayerSales'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const CoachingTerms = lazy(() => import('./pages/CoachingTerms'));
const NewPlayerProfile = lazy(() => import('./pages/NewPlayerProfile'));
const EventAdminDirect = lazy(() => import('./pages/EventAdminDirect'));
const Consultation = lazy(() => import('./pages/Consultation'));
const TournamentConsultation = lazy(() => import('./pages/TournamentConsultation'));
const Tournaments = lazy(() => import('./pages/Tournaments'));
const SuperLogin = lazy(() => import('./pages/SuperLogin'));
const RegistrationSuccess = lazy(() => import('./pages/RegistrationSuccess'));
const RegistrationCancelled = lazy(() => import('./pages/RegistrationCancelled'));
const PurchaseSuccess = lazy(() => import('./pages/PurchaseSuccess'));
const PurchaseCancelled = lazy(() => import('./pages/PurchaseCancelled'));
const DonationSuccess = lazy(() => import('./pages/DonationSuccess'));
const DonationCancelled = lazy(() => import('./pages/DonationCancelled'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const CheckoutAddress = lazy(() => import('./pages/CheckoutAddress'));
const MyOrders = lazy(() => import('./pages/MyOrders'));
const Cart = lazy(() => import('./pages/Cart'));
const ShopPage = lazy(() => import('./pages/ShopPage'));
const Admin = lazy(() => import('./pages/Admin'));
const EndorsementSubmit = lazy(() => import('./pages/EndorsementSubmit'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const AuthorBlogsPage = lazy(() => import('./pages/AuthorBlogsPage'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const TestImagePage = lazy(() => import('./pages/TestImagePage'));
// const ChesscomPage = lazy(() => import('./pages/ChesscomPage'));
const CrossTablePage = lazy(() => import('./pages/CrossTablePage'));
const CreateChessProduct = lazy(() => import('./pages/CreateChessProduct'));
const ChessGameVideoGenerator = lazy(() => import('./pages/ChessGameVideoGenerator'));
const EbookEditor = lazy(() => import('./pages/EbookEditor'));
const Ebooks = lazy(() => import('./pages/Ebooks'));
const ConfirmAccess = lazy(() => import('./pages/ConfirmAccess'));
const PublicEbookGenerator = lazy(() => import('./pages/PublicEbookGenerator'));

// Shared profile site layout (persistent header across /me, /coach, /library)
const ProfileSiteLayout = lazy(() => import('./components/layout/ProfileSiteLayout'));

// GroupChess pages
const GroupChessLayout = lazy(() => import('./components/groupchess/GroupChessLayout'));
const GroupChessLanding = lazy(() => import('./pages/groupchess/GroupChessLanding'));
const GroupChessCheckout = lazy(() => import('./pages/groupchess/GroupChessCheckout'));
const GroupChessDashboard = lazy(() => import('./pages/groupchess/GroupChessDashboard'));
const GroupChessSession = lazy(() => import('./pages/groupchess/GroupChessSession'));
const CreditPurchaseSuccess = lazy(() => import('./pages/groupchess/CreditPurchaseSuccess'));
const CreditPurchaseCancelled = lazy(() => import('./pages/groupchess/CreditPurchaseCancelled'));
const IndividualSessionCheckout = lazy(() => import('./pages/groupchess/IndividualSessionCheckout'));
const IndividualSessionSuccess = lazy(() => import('./pages/groupchess/IndividualSessionSuccess'));
const MembershipSuccess = lazy(() => import('./pages/groupchess/MembershipSuccess'));
const BillingPage = lazy(() => import('./pages/groupchess/BillingPage'));
const CoachOnboarding = lazy(() => import('./pages/groupchess/CoachOnboarding'));
const CoachLanding = lazy(() => import('./pages/groupchess/CoachLanding'));
const CoachLandingPage = lazy(() => import('./pages/CoachLandingPage'));
const CoachSignupPage = lazy(() => import('./pages/CoachSignupPage'));
const CoachMarketplaceOnboarding = lazy(() => import('./pages/CoachMarketplaceOnboarding'));
const AcademyPage = lazy(() => import('./pages/AcademyPage'));
const CoachDirectoryPage = lazy(() => import('./pages/groupchess/CoachDirectoryPage'));
const StudentQuestionnaire = lazy(() => import('./pages/groupchess/StudentQuestionnaire'));
const CoachQuestionnaire = lazy(() => import('./pages/groupchess/CoachQuestionnaire'));
const LessonDetailPage = lazy(() => import('./pages/groupchess/LessonDetailPage'));
const CoachWebsiteEditor = lazy(() => import('./pages/groupchess/CoachWebsiteEditor'));
const LibraryPage = lazy(() => import('./pages/groupchess/LibraryPage'));
const PuzzlePage = lazy(() => import('./pages/groupchess/PuzzlePage'));
const ContentViewerPage = lazy(() => import('./pages/groupchess/ContentViewerPage'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const ProfileLayoutEditor = lazy(() => import('./pages/ProfileLayoutEditor'));
const TestDashboard = lazy(() => import('./pages/TestDashboard'));
const UserAnalytics = lazy(() => import('./pages/UserAnalytics'));
const UserGuidePage = lazy(() => import('./pages/UserGuidePage'));
const GuidePage = lazy(() => import('./pages/GuidePage'));

// Dashboard shell + nested pages
const DashboardShell = lazy(() => import('./pages/dashboard/DashboardShell'));
const DashboardHome = lazy(() => import('./pages/dashboard/DashboardHome'));
const WebsitePortal = lazy(() => import('./pages/dashboard/WebsitePortal'));
const BlogsPage = lazy(() => import('./pages/dashboard/BlogsPage'));
const SocialLinksPage = lazy(() => import('./pages/dashboard/SocialLinksPage'));
const PremiumPage = lazy(() => import('./pages/dashboard/PremiumPage'));
const ReferralsPage = lazy(() => import('./pages/dashboard/ReferralsPage'));
const ThemePage = lazy(() => import('./pages/dashboard/ThemePage'));
const ContactPage = lazy(() => import('./pages/dashboard/ContactPage'));
const SubscribersPage = lazy(() => import('./pages/dashboard/SubscribersPage'));
const DonationsPage = lazy(() => import('./pages/dashboard/DonationsPage'));
const RevenuePage = lazy(() => import('./pages/dashboard/RevenuePage'));
const ShopDashboardPage = lazy(() => import('./pages/dashboard/ShopDashboardPage'));
const CoachingSetupPage = lazy(() => import('./pages/dashboard/CoachingSetupPage'));
const DashboardCoachingWrapper = lazy(() => import('./pages/dashboard/DashboardCoachingWrapper'));
const DashboardLibraryWrapper = lazy(() => import('./pages/dashboard/DashboardLibraryWrapper'));
const SettingsPage = lazy(() => import('./pages/dashboard/SettingsPage'));
const PaymentsPage = lazy(() => import('./pages/dashboard/PaymentsPage'));

// Inline SVG chess king — renders instantly without waiting for Font Awesome webfonts
const ChessKingSvg = ({ className }) => (
  <svg viewBox="0 0 448 512" fill="currentColor" className={className} aria-hidden="true">
    <path d="M224 0c17.7 0 32 14.3 32 32l0 16 16 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-16 0 0 48 152 0c22.1 0 40 17.9 40 40c0 5.3-1 10.5-3.1 15.4L368 400 80 400 3.1 215.4C1 210.5 0 205.3 0 200c0-22.1 17.9-40 40-40l152 0 0-48-16 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l16 0 0-16c0-17.7 14.3-32 32-32zM38.6 473.4L80 432l288 0 41.4 41.4c4.2 4.2 6.6 10 6.6 16c0 12.5-10.1 22.6-22.6 22.6L54.6 512C42.1 512 32 501.9 32 489.4c0-6 2.4-11.8 6.6-16z"/>
  </svg>
);

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center px-6 overflow-x-hidden"
    style={{
      background: `
        linear-gradient(45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%),
        linear-gradient(-45deg, rgba(255, 255, 255, 0.05) 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
        linear-gradient(-45deg, transparent 75%, rgba(255, 255, 255, 0.05) 75%),
        linear-gradient(to bottom, #1a1a1a 0%, #0d0d0d 90%)
      `,
      boxShadow: 'inset 0 2px 12px rgba(12, 10, 10, 0.6)'
    }}
  >
    <div className="flex flex-col items-center space-y-6 text-center">
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
          <ChessKingSvg className="w-8 h-8 text-white/30" />
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <ChessKingSvg className="w-8 h-8 text-white/50 animate-bounce" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * SmartTournamentsRoute - Shows Organizer page for authenticated users, landing page for guests
 */
function SmartTournamentsRoute() {
  const { currentUser } = useAuth();

  if (currentUser) {
    return <Organizer />;
  }

  return <Tournaments />;
}

/**
 * SmartCreatorProfilesRoute - Shows Dashboard for authenticated users, redirects guests to home
 */
function SmartCreatorProfilesRoute() {
  const { currentUser } = useAuth();

  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect guests to root (which shows CreatorProfiles landing page)
  return <Navigate to="/?context=creator" replace />;
}

/**
 * LibraryRedirect — redirect legacy /coach/:username/library/* to /library/:username/*
 */
function LibraryRedirect() {
  const { username, '*': rest } = useParams();
  const suffix = rest ? `/${rest}` : '';
  return <Navigate to={`/library/${username}${suffix}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  const { currentUser } = useAuth();

  // Corrected pageview for custom domains & subdomains.
  // Plausible auto-tracks pageviews with the browser URL, but on dedicated domains
  // the path (e.g. "/coaching") doesn't contain "/me/username", so per-user analytics
  // filters miss it. Fire a second pageview with a synthetic URL that includes the
  // /me/username prefix so the server-side filter catches it.
  const subdomainUser = isSubdomain() ? getSubdomainUsername() : null;
  const bareHostname = window.location.hostname.replace(/^www\./, '');
  const customDomainUser = customDomains[bareHostname];
  const dedicatedDomainUser = subdomainUser || customDomainUser;

  const prevPathRef = useRef(null);

  useEffect(() => {
    if (!dedicatedDomainUser || typeof window.plausible !== 'function') return;

    const path = location.pathname;
    // Skip if same path (avoid double-fire on re-renders)
    if (path === prevPathRef.current) return;
    prevPathRef.current = path;

    const userLower = dedicatedDomainUser.toLowerCase();
    // Build synthetic URL: chessweb.site/me/username + actual path
    // e.g. indiechess.com/coaching → chessweb.site/me/indiechess/coaching
    const suffix = path === '/' ? '' : path;
    const syntheticUrl = `https://chessweb.site/me/${userLower}${suffix}`;

    window.plausible('pageview', { u: syntheticUrl });
  }, [location.pathname, dedicatedDomainUser]);

  // Check if we're on chessclip.io domain - if so, only show video generator
  const isChessClipDomain = window.location.hostname === 'chessclip.io' || window.location.hostname === 'www.chessclip.io';

  if (isChessClipDomain) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <ChessGameVideoGenerator />
      </Suspense>
    );
  }

  // Check if we're on chessbook.io domain - if so, only show ebook generator
  const isChessBookDomain = window.location.hostname === 'chessbook.io' || window.location.hostname === 'www.chessbook.io';

  if (isChessBookDomain) {
    // Check for OAuth callback - preserve query params
    const hasOAuthCallback = location.search.includes('code=') && location.search.includes('state=');

    // Only redirect to / if not at root AND not an OAuth callback
    if (location.pathname !== '/' && !hasOAuthCallback) {
      return <Navigate to="/" replace />;
    }

    // If OAuth callback with non-root path, redirect to root with query params
    if (location.pathname !== '/' && hasOAuthCallback) {
      return <Navigate to={`/${location.search}`} replace />;
    }

    return (
      <Suspense fallback={<LoadingFallback />}>
        <PublicEbookGenerator />
      </Suspense>
    );
  }

  // Custom domain / subdomain detection — routes "/" and "/donate" through ProfileSiteLayout
  // so the persistent header stays mounted across all route transitions (no full reload).
  const hostname = window.location.hostname.replace(/^www\./, '');
  const customDomainUsername = customDomains[hostname];
  const ownDomainUsername = customDomainUsername || (isSubdomain() ? getSubdomainUsername() : null);
  const isOnOwnDomain = !!customDomainUsername || isSubdomain();

  // Hide header on all unauthenticated/public pages
  // Always show header for authenticated admin routes regardless of query params
  const authRoutes = ['/organizer', '/player', '/player-profiles', '/account', '/firebase-test', '/firebase-debug', '/browse', '/admin'];
  const isAuthRoute = authRoutes.some(route => location.pathname.startsWith(route));
  const isPremiumSales = location.pathname.startsWith('/premium');
  const isHomePage = location.pathname === '/';
  const isSampleData = location.pathname === '/SampleData0725';
  const isNewEventPage = location.pathname.startsWith('/newEvent');
  const isPlayerPage = location.pathname.startsWith('/playerPage');
  const isRegisterWithEventId = location.pathname === '/register' && location.search.includes('eventId=');
  const isPlayerProfilePreview = (location.pathname.startsWith('/me/') && !location.pathname.startsWith('/me/edit/') && location.pathname !== '/me') || (isSubdomain() && location.pathname === '/donate');
  const isPlayerEditMode = location.pathname.startsWith('/me/edit/');
  const isTournamentsPage = location.pathname === '/tournaments';
  const isCreatorProfilesPage = location.pathname === '/me';
  const isSubdomainHome = isHomePage && isSubdomain();
  const isBlogPage = location.pathname.startsWith('/blog/');
  const isAuthorBlogsPage = location.pathname.startsWith('/blogs/author/');
  const isAuthPage = location.pathname === '/auth';
  const isPurchasePage = location.pathname.startsWith('/purchase/');
  const isProductDetailPage = location.pathname.startsWith('/product/');
  const isMyOrdersPage = location.pathname === '/my-orders';
  const isCartPage = location.pathname === '/cart';
  const isShopPage = location.pathname.startsWith('/shop/');
  const isCheckoutAddressPage = location.pathname.startsWith('/checkout/address/');
  const isCreateChessProductPage = location.pathname === '/chess-merch';
  const isChessGameVideoGeneratorPage = location.pathname === '/chess-video-generator';
  const isConfirmAccessPage = location.pathname.startsWith('/confirm-access/');
  const isPublicEbookPage = location.pathname === '/ebook';
  const isHiddenRoute = location.pathname.startsWith('/hidden') || location.pathname.startsWith('/coach') || location.pathname.startsWith('/library');
  const isDashboardPage = location.pathname.startsWith('/dashboard');
  const isOrganizerPage = location.pathname === '/organizer';
  const isAccountPage = location.pathname === '/account';
  const isPremiumPage = location.pathname === '/premium';
  const isPlayerProfilesPage = location.pathname === '/player-profiles';
  const isUserGuidePage = location.pathname === '/user-guide';
  const isAnalyticsPage = location.pathname === '/analytics';

  // For smart routes (/tournaments and /me), only hide header if unauthenticated
  const isUnauthenticatedTournamentsPage = isTournamentsPage && !currentUser;
  const isUnauthenticatedCreatorProfilesPage = isCreatorProfilesPage && !currentUser;
  const isAuthenticatedTournamentsPage = isTournamentsPage && currentUser;
  const isAuthenticatedCreatorProfilesPage = isCreatorProfilesPage && currentUser;

  // Hide header for all routes except explicit auth routes and sample data
  const showHeader = isAuthRoute || isSampleData;
  const hideHeader = !showHeader;
  return (
    <div className="App">
      {(isHomePage || !hideHeader) && (
        <div style={{ position: 'sticky', top: 0, zIndex: 50 }}>
          {isHomePage && !isOnOwnDomain && (
            <div
              style={{
                backgroundColor: '#ffd700',
                color: '#1a1a1a',
                textAlign: 'center',
                padding: '6px 16px',
                fontSize: '15px',
                fontWeight: 600,
                letterSpacing: '0.3px',
              }}
            >
              Want a live demo?{' '}
              <a
                href="https://cal.com/indiechess/consult"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1a1a1a',
                  textDecoration: 'underline',
                  fontWeight: 700,
                }}
              >
                Book a call &rarr;
              </a>
            </div>
          )}
          {!hideHeader && <NavigationHeader />}
        </div>
      )}
      <CartIcon />
      <CartSidebar />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* Main site root — only when NOT on a custom domain/subdomain (those use ProfileSiteLayout above) */}
        {!isOnOwnDomain && <Route path="/" element={<CreatorProfiles />} />}
        <Route path="/auth" element={<Auth />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/superlogin" element={<SuperLogin />} />
        <Route path="/auth-test" element={<AuthTest />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardShell /></ProtectedRoute>}>
          <Route index element={<DashboardHome />} />
          <Route path="website" element={<WebsitePortal />} />
          <Route path="website/blogs" element={<BlogsPage />} />
          <Route path="website/social-links" element={<SocialLinksPage />} />
          <Route path="website/premium" element={<PremiumPage />} />
          <Route path="website/referrals" element={<ReferralsPage />} />
          <Route path="website/theme" element={<ThemePage />} />
          <Route path="website/contact" element={
            <PremiumGate icon="fa-envelope" title="Direct Messages"
              description="Receive messages from visitors directly through your chess website — with auto-subscribe and email replies."
              benefitsTitle="Premium Direct Messages Include"
              benefits={[
                { icon: 'fa-envelope-open-text', text: 'Contact form on your public profile' },
                { icon: 'fa-user-check', text: 'Sender name, email & auto-subscribe' },
                { icon: 'fa-reply', text: 'Reply directly via email' },
                { icon: 'fa-shield-halved', text: 'Rate limiting to prevent spam' },
              ]}>
              <ContactPage />
            </PremiumGate>
          } />
          <Route path="website/subscribers" element={<SubscribersPage />} />
          <Route path="website/donations" element={
            <PremiumGate icon="fa-heart" title="Donations & Tips"
              description="Let visitors support you directly — accept tips and donations from your chess website."
              benefitsTitle="Premium Donations Include"
              benefits={[
                { icon: 'fa-heart', text: 'Accept tips and donations on your website' },
                { icon: 'fa-credit-card', text: 'Stripe Connect payments built in' },
                { icon: 'fa-sliders', text: 'Customizable preset donation amounts' },
                { icon: 'fa-toggle-on', text: 'Donation toggle on your public profile' },
              ]}>
              <DonationsPage />
            </PremiumGate>
          } />
          <Route path="revenue" element={
            <PremiumGate icon="fa-dollar-sign" title="Revenue Dashboard"
              description="Track all your earnings across coaching, sessions, memberships, and more."
              benefitsTitle="Premium Revenue Includes"
              benefits={[
                { icon: 'fa-chart-bar', text: 'All-time and period revenue tracking' },
                { icon: 'fa-layer-group', text: 'Revenue breakdown by source' },
                { icon: 'fa-list', text: 'Unified transaction history' },
                { icon: 'fa-credit-card', text: 'Stripe Connect status and link' },
              ]}>
              <RevenuePage />
            </PremiumGate>
          } />
          <Route path="stripe" element={<PaymentsPage />} />
          <Route path="website/shop" element={
            <PremiumGate icon="fa-store" title="Digital Shop"
              description="Sell products, courses, and merchandise directly from your chess website."
              benefitsTitle="Premium Shop Includes"
              benefits={[
                { icon: 'fa-store', text: 'Sell products & merch on your website' },
                { icon: 'fa-credit-card', text: 'Stripe Connect payments built in' },
                { icon: 'fa-print', text: 'Print-on-demand products (no inventory)' },
                { icon: 'fa-chart-bar', text: 'Sales analytics & order management' },
              ]}>
              <ShopDashboardPage />
            </PremiumGate>
          } />
          <Route path="coach/:username" element={<CoachingSetupPage />} />
          <Route path="coaching" element={<Navigate to="/dashboard/coach" replace />} />
          <Route path="coach" element={
            <PremiumGate icon="fa-chalkboard-user" title="Coaching Academy"
              description="Host your coaching academy from your website — with scheduling, payments, and student management."
              benefitsTitle="Premium Coaching Includes"
              benefits={[
                { icon: 'fa-calendar-check', text: 'Lesson scheduling with payments' },
                { icon: 'fa-users', text: 'Student management & credited billing' },
                { icon: 'fa-globe', text: 'Public coaching page on your website' },
                { icon: 'fa-chart-bar', text: 'Coaching revenue analytics' },
              ]}>
              <DashboardCoachingWrapper />
            </PremiumGate>
          } />
          <Route path="library" element={
            <PremiumGate icon="fa-book" title="Content Library"
              description="Upload and organize chess content — PGN files, puzzles, videos, and study materials for your students."
              benefitsTitle="Premium Library Includes"
              benefits={[
                { icon: 'fa-folder-tree', text: 'Organize content in folders' },
                { icon: 'fa-puzzle-piece', text: 'Create interactive puzzles' },
                { icon: 'fa-upload', text: 'Upload PGN, PDF, and media files' },
                { icon: 'fa-lock', text: 'Control access & link to products' },
              ]}>
              <DashboardLibraryWrapper />
            </PremiumGate>
          } />
          <Route path="analytics" element={
            <PremiumGate icon="fa-chart-line" title="Analytics"
              description="See how your website, coaching, and shop are performing — all in one dashboard."
              benefitsTitle="Premium Analytics Includes"
              benefits={[
                { icon: 'fa-chart-line', text: 'Website traffic & visitor data' },
                { icon: 'fa-money-bill-trend-up', text: 'Coaching & shop revenue tracking' },
                { icon: 'fa-magnifying-glass-chart', text: 'Page-level performance insights' },
                { icon: 'fa-clock', text: 'Historical trends over time' },
              ]}>
              <UserAnalytics />
            </PremiumGate>
          } />
          <Route path="tournaments" element={<Organizer embedded />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="user-guide" element={<UserGuidePage />} />
        </Route>
        <Route path="/analytics" element={<Navigate to="/dashboard/analytics" replace />} />
        <Route path="/user-guide" element={<Navigate to="/dashboard/user-guide" replace />} />
        <Route path="/guide" element={<GuidePage />} />
        <Route path="/organizer" element={<ProtectedRoute><Organizer /></ProtectedRoute>} />
        <Route path="/player" element={<ProtectedRoute><Player /></ProtectedRoute>} />
        <Route path="/player-profiles" element={<ProtectedRoute><PlayerProfiles /></ProtectedRoute>} />
        <Route path="/newEvent" element={<ProtectedRoute><NewEvent /></ProtectedRoute>} />
        <Route path="/playerPage" element={<ProtectedRoute><NewPlayerProfile /></ProtectedRoute>} />
        <Route path="/register" element={<Register />} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/registration-cancelled" element={<RegistrationCancelled />} />
        <Route path="/purchase/success" element={<PurchaseSuccess />} />
        <Route path="/purchase/cancelled" element={<PurchaseCancelled />} />
        <Route path="/donation/success" element={<DonationSuccess />} />
        <Route path="/donation/cancelled" element={<DonationCancelled />} />
        <Route path="/confirm-access/:purchaseId/:token" element={<ConfirmAccess />} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
        <Route path="/premium" element={<Premium />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/premium" element={<ProtectedRoute><PremiumSales /></ProtectedRoute>} />
        <Route path="/premium-sales" element={<ProtectedRoute><PremiumSales /></ProtectedRoute>} />
        <Route path="/premium-player" element={<ProtectedRoute><Navigate to="/dashboard/website/premium" replace /></ProtectedRoute>} />
        {/* <Route path="/social" element={<ChesscomPage />} /> */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/coaching-terms" element={<CoachingTerms />} />
        <Route path="/consultation" element={<Consultation />} />
        <Route path="/tournament-consultation" element={<TournamentConsultation />} />
        <Route path="/tournament" element={<SmartTournamentsRoute />} />
        <Route path="/tournaments" element={<SmartTournamentsRoute />} />
        <Route path="/SampleData0725" element={<EventPreview eventId="SampleData0725" />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/me" element={<SmartCreatorProfilesRoute />} />
        <Route path="/me/edit/:username" element={<NewPlayerProfile />} />
        <Route path="/me/:username/endorse" element={<EndorsementSubmit />} />
        <Route path="/me/edit/:username/layout/:pageSlug" element={<ProfileLayoutEditor />} />
        <Route path="/me/edit/:username/layout" element={<ProfileLayoutEditor />} />
        {/* ProfileSiteLayout — persistent header across /me, /coach, /library routes */}
        {/* On custom domains/subdomains, "/" and "/donate" also go through this layout */}
        <Route element={<ProfileSiteLayout />}>
          {isOnOwnDomain && <Route path="/" element={<PlayerProfilePreview username={ownDomainUsername} hideHeader />} />}
          {isOnOwnDomain && <Route path="/donate" element={<PlayerProfilePreview username={ownDomainUsername} openDonationOnMount={true} hideHeader />} />}
          <Route path="/me/:username" element={<PlayerProfilePreview hideHeader />} />
          <Route path="/me/:username/donate" element={<PlayerProfilePreview openDonationOnMount={true} hideHeader />} />

          {/* New: /me/:username/library routes (username in profile prefix) */}
          <Route path="/me/:username/library/puzzle/:puzzleId" element={<GroupChessLayout hideHeader><PuzzlePage /></GroupChessLayout>} />
          <Route path="/me/:username/library/view/:itemId" element={<GroupChessLayout hideHeader><ContentViewerPage /></GroupChessLayout>} />
          <Route path="/me/:username/library/:folderId" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />
          <Route path="/me/:username/library" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />

          {/* New: /me/:username/coach routes (username in profile prefix) */}
          <Route path="/me/:username/coach/lesson/:lessonId" element={<GroupChessLayout hideHeader><LessonDetailPage /></GroupChessLayout>} />
          <Route path="/me/:username/coach" element={<GroupChessLayout hideHeader><GroupChessLanding /></GroupChessLayout>} />
          <Route path="/me/:username/coach/checkout" element={<GroupChessLayout hideHeader><GroupChessCheckout /></GroupChessLayout>} />
          <Route path="/me/:username/coach/credits/success" element={<GroupChessLayout hideHeader><CreditPurchaseSuccess /></GroupChessLayout>} />
          <Route path="/me/:username/coach/credits/cancelled" element={<GroupChessLayout hideHeader><CreditPurchaseCancelled /></GroupChessLayout>} />
          <Route path="/me/:username/coach/individual/checkout" element={<GroupChessLayout hideHeader><IndividualSessionCheckout /></GroupChessLayout>} />
          <Route path="/me/:username/coach/individual/success" element={<GroupChessLayout hideHeader><IndividualSessionSuccess /></GroupChessLayout>} />
          <Route path="/me/:username/coach/membership/success" element={<GroupChessLayout hideHeader><MembershipSuccess /></GroupChessLayout>} />
          <Route path="/me/:username/coach/dashboard" element={<GroupChessLayout hideHeader variant="dashboard"><GroupChessDashboard /></GroupChessLayout>} />

          {/* Custom-domain-only clean routes — no username in URL */}
          {isOnOwnDomain && <Route path="/library/puzzle/:puzzleId" element={<GroupChessLayout hideHeader><PuzzlePage /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/library/view/:itemId" element={<GroupChessLayout hideHeader><ContentViewerPage /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/library/:folderId" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/library" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/lesson/:lessonId" element={<GroupChessLayout hideHeader><LessonDetailPage /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach" element={<GroupChessLayout hideHeader><GroupChessLanding /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/checkout" element={<GroupChessLayout hideHeader><GroupChessCheckout /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/credits/success" element={<GroupChessLayout hideHeader><CreditPurchaseSuccess /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/credits/cancelled" element={<GroupChessLayout hideHeader><CreditPurchaseCancelled /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/individual/checkout" element={<GroupChessLayout hideHeader><IndividualSessionCheckout /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/individual/success" element={<GroupChessLayout hideHeader><IndividualSessionSuccess /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/membership/success" element={<GroupChessLayout hideHeader><MembershipSuccess /></GroupChessLayout>} />}
          {isOnOwnDomain && <Route path="/coach/dashboard" element={<GroupChessLayout hideHeader variant="dashboard"><GroupChessDashboard /></GroupChessLayout>} />}

          {/* Legacy: /library/:username and /coach/:username routes (still functional) */}
          <Route path="/library/:username" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />
          <Route path="/library/:username/puzzle/:puzzleId" element={<GroupChessLayout hideHeader><PuzzlePage /></GroupChessLayout>} />
          <Route path="/library/:username/view/:itemId" element={<GroupChessLayout hideHeader><ContentViewerPage /></GroupChessLayout>} />
          <Route path="/library/:username/:folderId" element={<GroupChessLayout hideHeader><LibraryPage /></GroupChessLayout>} />

          {/* Shareable lesson detail page */}
          <Route path="/coach/:username/lesson/:lessonId" element={<GroupChessLayout hideHeader><LessonDetailPage /></GroupChessLayout>} />

          {/* Legacy username-based coach routes */}
          <Route path="/coach/:username" element={<GroupChessLayout hideHeader><GroupChessLanding /></GroupChessLayout>} />
          <Route path="/coach/:username/checkout" element={<GroupChessLayout hideHeader><GroupChessCheckout /></GroupChessLayout>} />
          <Route path="/coach/:username/credits/success" element={<GroupChessLayout hideHeader><CreditPurchaseSuccess /></GroupChessLayout>} />
          <Route path="/coach/:username/credits/cancelled" element={<GroupChessLayout hideHeader><CreditPurchaseCancelled /></GroupChessLayout>} />
          <Route path="/coach/:username/individual/checkout" element={<GroupChessLayout hideHeader><IndividualSessionCheckout /></GroupChessLayout>} />
          <Route path="/coach/:username/individual/success" element={<GroupChessLayout hideHeader><IndividualSessionSuccess /></GroupChessLayout>} />
          <Route path="/coach/:username/membership/success" element={<GroupChessLayout hideHeader><MembershipSuccess /></GroupChessLayout>} />
          <Route path="/coach/:username/dashboard" element={<GroupChessLayout hideHeader variant="dashboard"><GroupChessDashboard /></GroupChessLayout>} />

          {/* Blog routes inside layout — persistent header for custom-layout profiles */}
          <Route path="/me/:username/blog/:slug" element={<BlogPage embedded hideHeader />} />
          <Route path="/me/:username/blog" element={<AuthorBlogsPage embedded hideHeader />} />
          {isOnOwnDomain && <Route path="/blog/:slug" element={<BlogPage embedded hideHeader username={ownDomainUsername} />} />}
          {isOnOwnDomain && <Route path="/blog" element={<AuthorBlogsPage embedded hideHeader username={ownDomainUsername} />} />}

          {/* Multi-page catch-all routes — MUST come after all specific routes */}
          <Route path="/me/:username/:pageSlug" element={<PlayerProfilePreview hideHeader />} />
          {isOnOwnDomain && <Route path="/:pageSlug" element={<PlayerProfilePreview username={ownDomainUsername} hideHeader />} />}
        </Route>

        {/* Routes outside the shared layout (standalone) */}
        {/* Backward compatibility - old blog ID format (skip on custom domains where /blog/:slug is handled inside layout) */}
        {!isOnOwnDomain && <Route path="/blog/:blogId" element={<BlogPage />} />}
        <Route path="/blogs/author/:username" element={<AuthorBlogsPage />} />
        {/* Product detail page */}
        <Route path="/product/:username/:productId" element={<ProductDetail />} />
        {/* Checkout address page for POD products */}
        <Route path="/checkout/address/:profileId/:productId/:variantId" element={<CheckoutAddress />} />
        {/* Order tracking page */}
        <Route path="/my-orders" element={<MyOrders />} />
        {/* Shopping cart page */}
        <Route path="/cart" element={<Cart />} />
        {/* Shop page - all products from a vendor */}
        <Route path="/shop/:username" element={<ShopPage />} />
        {/* Custom chess product builder */}
        <Route path="/chess-merch" element={<CreateChessProduct />} />
        {/* Chess game video generator */}
        <Route path="/chess-video-generator" element={<ChessGameVideoGenerator />} />
        {/* Ebook management */}
        {/* <Route path="/ebook-editor" element={<ProtectedRoute><EbookEditor /></ProtectedRoute>} /> */}
        {/* Public ebook generator - no auth required */}
        <Route path="/ebook" element={<PublicEbookGenerator />} />
        <Route path="/ebooks" element={<PublicEbookGenerator />} />

        {/* Coach questionnaire funnels — must be above /coaches to match first */}
        <Route path="/coaches/get-started" element={<StudentQuestionnaire />} />
        <Route path="/coaches/for-coaches" element={<CoachQuestionnaire />} />

        {/* Coach directory — aggregated student-facing page */}
        <Route path="/coaches" element={<CoachDirectoryPage />} />

        {/* Standalone academy for marketplace coaches */}
        <Route path="/academy" element={<AcademyPage />} />

        {/* Coach routes - static paths must come before dynamic :username */}
        <Route path="/coach/signup" element={<CoachSignupPage />} />
        <Route path="/coach/onboarding" element={<CoachMarketplaceOnboarding />} />
        <Route path="/coach" element={<CoachLandingPage />} />
        {/* TODO: Future — individual coach SEO pages at /coaches/[slug] with JSON-LD Person markup */}

        {/* Legacy library redirects */}
        <Route path="/coach/:username/library" element={<LibraryRedirect />} />
        <Route path="/coach/:username/library/*" element={<LibraryRedirect />} />

        {/* Standalone coach routes (no shared header) */}
        <Route path="/coach/:username/admin" element={<Navigate to="/dashboard/coach" replace />} />
        <Route path="/coach/:username/session/:lessonId" element={<GroupChessSession />} />

        <Route path="/explore" element={<Navigate to="/" replace />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/test-image" element={<TestImagePage />} />
        <Route path="/cross-table" element={<CrossTablePage />} />
        <Route
          path="/:eventSlug/admin"
          element={
            <ProtectedRoute>
              <EventAdminDirect />
            </ProtectedRoute>
          }
        />
        {/* <Route path="/firebase-test" element={<FirebaseTest />} />
        <Route path="/firebase-debug" element={<FirebaseDebug />} /> */}
        <Route path="/:eventId" element={<CustomUrlRedirect />} />
        <Route path="*" element={<CustomUrlRedirect />} />
      </Routes>
      </Suspense>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <Router>
            <ScrollToTop />
            <AppRoutes />
          </Router>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
